import React, { useEffect } from "react";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import ViewSurveyResultModal from "@iso/containers/Pages/Form/ViewSurveyResultModal";
import IntlMessages from "@iso/components/utility/intlMessages";
import { useSelector, useDispatch } from "react-redux";
import {
  Button, Dropdown, Menu, Select, Pagination, Input, Spin, Empty, Popconfirm, message, Popover, Tag, Tooltip,
} from "antd";
import VisitorPageWrapper from "./Visitor.style";
import {
  SearchOutlined,
  CloseCircleOutlined,
  EllipsisOutlined,
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  SendOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  UserDeleteOutlined,
  SafetyCertificateOutlined,
  EnvironmentOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import propertyActions from "@iso/redux/property/actions";
import Loader from "@iso/components/utility/loader.js";
import _ from "lodash";
import moment from "moment";
import VisitorInfoModal from "./VisitorInfoModal";
import RunInductionModal from "./RunInductionModal";
import { visitorInfoService } from "../../../services/visitorInfo.service";
import { propertyService } from "../../../services/property.service";
import VisitorViewSummary from "./VisitorViewSummary";
import UserAccessModal from "../Property/UserAccessModal";
import { commonHelper } from "@iso/lib/helpers/commonHelper";
import {
  VISITOR_STATUS,
  VISITOR_STATUS_META,
  VISITOR_STATUS_OPTIONS,
  VISITOR_TYPE_CLASS,
  VISITOR_TABS,
  getVisitorStatus,
  getVisitorType,
  getVisitorInductionState,
  INDUCTION_STATE,
  INDUCTION_STATE_META,
  getInductionBadgeLabel,
  getInductionBadgeHint,
  isAwaitingVerification,
  INDUCTION_FILTER_OPTIONS,
} from "@iso/constants/visitor";

const { changeActiveProperty } = propertyActions;

// division_contact_status -> tag. "failed" means the contractor reported they
// couldn't reach their division contact.
const DIVISION_CONTACT_TAG = {
  awaiting: { color: "orange", label: "Awaiting contact" },
  contractor_confirmed: { color: "gold", label: "Contractor says contact made" },
  verified: { color: "green", label: "Confirmed" },
  failed: { color: "red", label: "Contact not made" },
};

/* ------------------------------------------------------------------ */
/* Presentation-only helpers. Domain rules (status/type/induction)    */
/* live in constants/visitor.js so every consumer stays in sync.      */
/* ------------------------------------------------------------------ */

// The stat cards, tab counts, and type-filter options are whole-property
// aggregates the backend now computes in one query and returns as
// `summary`; the table itself is paginated + filtered server-side. This
// replaced an older "fetch every row (pageSize 1000) and aggregate in the
// browser" approach that hung on properties with many visitors.
const SEARCH_DEBOUNCE_MS = 400;

const AVATAR_COLORS = [
  "#2563eb", "#16a34a", "#ea580c", "#9333ea",
  "#0891b2", "#dc2626", "#ca8a04", "#4f46e5",
];

const avatarColor = (name) => {
  const s = name || "?";
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const initials = (name) => {
  const parts = (name || "").trim().split(/\s+/);
  if (!parts[0]) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

const fmtDateTime = (value) =>
  value ? moment(value).format("DD MMM YYYY, hh:mm a") : "—";

const Visitor = (props) => {
  const dispatch = useDispatch();
  const activePropertyId = useSelector(
    (state) => state.property.activePropertyId
  );
  const activePropertySubscription = useSelector(
    (state) => state.property.activePropertySubscription
  );
  // Who is looking. Used only to decide whether the person who RAN an induction
  // is the one viewing it — they may accept their own, flagged as self-verified.
  const currentUser = useSelector((state) => state.user.user);
  const activePermission = useSelector(
    (state) => state.permission.activePermission
  );
  const [visibleVisitorModal, setVisibleVisitorModal] = React.useState(false);
  const [visibleVisitorFromResultModal, setVisibleVisitorFromResultModal] =
    React.useState(false);
  const activeProperty = commonHelper.getActiveProperty();
  const [properties, setProperties] = React.useState([]);
  const [isSaved] = React.useState(false);
  const [visitors, setVisitors] = React.useState([]);
  const [visitorData, setVisitorData] = React.useState({});
  const [visitorForm, setVisitorForm] = React.useState({});
  const [viewSummary, setViewSummary] = React.useState({});
  const [visibleModal, setvisibleModal] = React.useState(false);
  const [visibleViewSummaryForm, setVisibleViewSummaryForm] =
    React.useState(false);
  const [loadingData, setLoadingData] = React.useState(true);
  const [searchInput, setsearchInput] = React.useState("");
  const [activeTab, setActiveTab] = React.useState("visitors");
  const [typeFilter, setTypeFilter] = React.useState("all");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [inductionFilter, setInductionFilter] = React.useState("all");
  const [checkingOutId, setCheckingOutId] = React.useState(null);
  const [confirmingContactId, setConfirmingContactId] = React.useState(null);
  const [inductionVisitor, setInductionVisitor] = React.useState(null);
  const [visibleInductionModal, setVisibleInductionModal] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  // Server-side paging state: the table shows exactly the page the backend
  // returns; the cards/tabs/type-filter read the whole-property `summary`.
  const [totalCount, setTotalCount] = React.useState(0);
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  // Full-page loader only for the very first fetch; later refetches (tab,
  // filter, page, search) show a lighter table-level spinner so the cards
  // and — importantly — the search box stay mounted and keep focus.
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  const [summary, setSummary] = React.useState({
    total: 0,
    onsite: 0,
    expected_today: 0,
    pending_induction: 0,
    tab_counts: { visitors: 0, contractors: 0, inductions: 0, schedule: 0 },
    type_options: [],
  });

  // The app shell's <Content> paints a gray canvas (inline style in
  // PrivateLayout). The new visitor design is a flat white page, so
  // override it for the lifetime of this page and restore the previous
  // value on unmount — other pages keep their gray.
  useEffect(() => {
    const el = document.querySelector(".isomorphicContent");
    if (!el) return undefined;
    const prev = el.style.background;
    el.style.background = "#ffffff";
    return () => {
      el.style.background = prev;
    };
  }, []);

  useEffect(() => {
    if (props?.handleVisitorData) {
      props?.handleVisitorData(visitors ? visitors?.length : 0);
    }
  }, [visitors]);

  // One page of visitors + the whole-property summary, both from the
  // backend. Re-runs whenever the property, page, page size, tab, filters
  // or (debounced) search change — every one of those is a server-side
  // concern now, so there's no client-side filtering/slicing left.
  const updateVisitorData = React.useCallback(() => {
    if (!activePropertyId) return;
    setLoadingData(true);
    const params = {
      property_id: activePropertyId,
      page: currentPage,
      pageSize,
      tab: activeTab,
      type: typeFilter,
      status: statusFilter,
      induction: inductionFilter,
    };
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    visitorInfoService
      .getList("", params)
      .then((res) => {
        if (res && res.code === "0000") {
          setVisitors(res.visitors || []);
          setTotalCount(res.total_count || 0);
          if (res.summary) setSummary(res.summary);
        }
        setLoadingData(false);
        setHasLoadedOnce(true);
      })
      .catch(() => {
        setLoadingData(false);
        setHasLoadedOnce(true);
      });
  }, [
    activePropertyId,
    currentPage,
    pageSize,
    activeTab,
    typeFilter,
    statusFilter,
    inductionFilter,
    debouncedSearch,
  ]);

  useEffect(() => {
    updateVisitorData();
  }, [updateVisitorData]);

  // Debounce the search box into `debouncedSearch` (which the fetch above
  // depends on) and snap back to page 1 so results aren't hidden past the
  // end of a stale page.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    propertyService.getProperties().then((res) => {
      const propertyList = res?.properties?.data || res?.properties || [];
      if (propertyList.length > 0) {
        setProperties(propertyList);
        if (propertyList.length === 1) {
          localStorage.setItem(
            "active_property",
            JSON.stringify(propertyList[0])
          );
          dispatch(changeActiveProperty(propertyList[0]));
        }
      }
    }).catch((err) => {
      console.error("Failed to load properties for visitor page:", err);
    });
  }, [activePropertyId, isSaved, activePropertySubscription]);

  /* ------------------------- derived data ------------------------- */

  // The stat cards, tab counts, and type-filter options are whole-property
  // aggregates the backend computes and returns as `summary`. The table
  // rows are the already-paginated, already-filtered page the backend
  // returned — no client-side filtering or slicing remains.
  const stats = {
    total: summary.total,
    onsite: summary.onsite,
    expectedToday: summary.expected_today,
    pendingInduction: summary.pending_induction,
  };

  const tabCounts = summary.tab_counts || {
    visitors: 0,
    contractors: 0,
    inductions: 0,
    schedule: 0,
  };

  const typeOptions = summary.type_options || [];

  const displayedVisitors = visitors;

  // Changing a tab or dropdown filter snaps back to page 1 (batched with
  // the filter change, so the list refetches once) — otherwise a filtered
  // result set could sit past the end of a stale page.
  const changeTab = (key) => {
    setActiveTab(key);
    setCurrentPage(1);
  };
  const changeTypeFilter = (value) => {
    setTypeFilter(value);
    setCurrentPage(1);
  };
  const changeStatusFilter = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };
  const changeInductionFilter = (value) => {
    setInductionFilter(value);
    setCurrentPage(1);
  };

  /* --------------------------- handlers --------------------------- */

  const showModal = () => setVisibleVisitorModal(true);

  const cancelModal = () => {
    setVisibleVisitorModal(false);
    setVisitorData({});
  };

  const openEditModal = (visitor) => {
    setVisitorData(visitor);
    setVisibleVisitorModal(true);
  };

  const handleManualCheckOut = (visitor) => {
    setCheckingOutId(visitor.id);
    visitorInfoService
      .checkOutById(visitor.id)
      .then((res) => {
        if (res && res.code === "0000") {
          message.success(`${visitor.name || "Visitor"} checked out`);
          updateVisitorData();
        } else {
          message.error(res?.message || "Failed to check out visitor");
        }
      })
      .catch(() => message.error("Failed to check out visitor"))
      .finally(() => setCheckingOutId(null));
  };

  // Who may confirm is decided server-side (division contact, main office
  // users, owner/admin) — a refusal comes back as 0003 with a message.
  const handleConfirmDivisionContact = (visitor) => {
    setConfirmingContactId(visitor.id);
    visitorInfoService
      .confirmDivisionContact(visitor.id)
      .then((res) => {
        if (res && res.code === "0000") {
          message.success("Contact confirmed");
          if (res.visitor) {
            setVisitors((prev) => prev.map((row) => (row.id === visitor.id ? { ...row, ...res.visitor } : row)));
          } else {
            updateVisitorData();
          }
        } else {
          message.error(res?.message || "Failed to confirm contact");
        }
      })
      .catch(() => message.error("Failed to confirm contact"))
      .finally(() => setConfirmingContactId(null));
  };

  const menuList = (visitor) => (
    <Menu onClick={handleMenuClick.bind(this, visitor)}>
      <Menu.Item key="editDetails" icon={<EditOutlined />}>
        <IntlMessages id="propertyPage.managePage.editDetails" />
      </Menu.Item>
      {visitor.visitor_form &&
        ["self_create", "system_forms", "bio_form", "checkin_form"].includes(
          visitor?.visitor_form?.form?.type
        ) && (
          <Menu.Item key="viewResult" icon={<EyeOutlined />}>
            <IntlMessages id="visitorPage.table.button.viewFormResult" />
          </Menu.Item>
        )}
      <Menu.Item key="runInduction" icon={<SafetyCertificateOutlined />}>
        <IntlMessages id="Run Induction" />
      </Menu.Item>
      {/* Only shown when there is actually something waiting. It opens the
          summary drawer, where the answers and the Approve/Reject controls sit
          together — a verifier should not be able to accept something they have
          not seen, so there is no accept button out here on the row. */}
      {isAwaitingVerification(visitor.induction_summary) && (
        <Menu.Item key="verifyInduction" icon={<SafetyCertificateOutlined />}>
          <IntlMessages id="Verify Induction" />
        </Menu.Item>
      )}
      <Menu.Item key="invite" icon={<SendOutlined />}>
        <IntlMessages id="Invite" />
      </Menu.Item>
      <Menu.Item key="viewSummary" icon={<EyeOutlined />}>
        <IntlMessages id="View Summary" />
      </Menu.Item>
    </Menu>
  );

  const handleMenuClick = (visitor, e) => {
    if (e.key === "invite") {
      setvisibleModal(true);
      setViewSummary(visitor);
    }
    if (e.key === "viewSummary") {
      setViewSummary(visitor);
      setVisibleViewSummaryForm(true);
    }
    if (e.key === "viewResult") {
      openFormResult(visitor.visitor_form);
    }
    if (e.key === "editDetails") {
      openEditModal(visitor);
    }
    if (e.key === "runInduction") {
      openInductionModal(visitor);
    }
    if (e.key === "verifyInduction") {
      setViewSummary(visitor);
      setVisibleViewSummaryForm(true);
    }
  };

  // Running an induction is deliberately NOT gated to owner/admin like manual
  // check-out is: the person meeting the visitor at the gate is usually a host.
  const openInductionModal = (visitor) => {
    setInductionVisitor(visitor);
    setVisibleInductionModal(true);
  };

  const closeInductionModal = () => {
    setVisibleInductionModal(false);
    setInductionVisitor(null);
  };

  const openFormResult = (form) => {
    setVisitorForm(form);
    setVisibleVisitorFromResultModal(true);
  };

  const cancelViewSurveyResult = () => {
    setVisitorForm({});
    setVisibleVisitorFromResultModal(false);
  };

  const handleOptionClick = (value) => {
    _.forEach(properties, (property) => {
      if (property.id == value) {
        localStorage.setItem("active_property", JSON.stringify(property));
        dispatch(changeActiveProperty(property));
      }
    });
  };

  /* --------------------------- renderers -------------------------- */

  const renderOptionsProperty = () => {
    const isLoading = !properties || properties.length === 0;
    return (
      <div style={{ position: "relative", display: "inline-block" }}>
        {isLoading && (
          <div className="property-select-loading">
            <Spin />
          </div>
        )}
        <Select
          placeholder="Select Active Property"
          value={activeProperty?.name}
          size="large"
          className="property-option"
          onChange={handleOptionClick}
          style={{ width: "100%" }}
          disabled={isLoading}
        >
          {properties?.map((property) => (
            <Select.Option
              key={property.id}
              value={property.id}
              className="property-option"
            >
              {property.name}
            </Select.Option>
          ))}
        </Select>
      </div>
    );
  };

  const renderStatCards = () => (
    <div className="stat-cards">
      <div className="stat-card">
        <div className="stat-icon icon-total"><TeamOutlined /></div>
        <div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total visitors</div>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon icon-onsite"><CheckCircleOutlined /></div>
        <div>
          <div className="stat-value">{stats.onsite}</div>
          <div className="stat-label">On site now</div>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon icon-expected"><ClockCircleOutlined /></div>
        <div>
          <div className="stat-value">{stats.expectedToday}</div>
          {/* Day-scoped via scheduled_arrival (falls back to created_at
              for legacy rows). The Schedule tab shows ALL upcoming
              arrivals, so its count can legitimately exceed this. */}
          <div className="stat-label">Expected today</div>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon icon-pending"><WarningOutlined /></div>
        <div>
          <div className="stat-value">{stats.pendingInduction}</div>
          <div className="stat-label">Pending induction</div>
        </div>
      </div>
    </div>
  );

  const renderTabs = () => (
    <div className="visitor-tabs">
      {VISITOR_TABS.map((t) => (
        <button
          key={t.key}
          type="button"
          className={`visitor-tab ${activeTab === t.key ? "active" : ""}`}
          onClick={() => changeTab(t.key)}
        >
          {t.label}
          <span className={`tab-count ${activeTab === t.key ? "active" : ""}`}>
            {tabCounts[t.key]}
          </span>
        </button>
      ))}
    </div>
  );

  const renderFilterBar = () => (
    <div className="filter-bar">
      <Input
        placeholder="Search visitors…"
        prefix={<SearchOutlined className="search-icon-prefix" />}
        suffix={
          searchInput && (
            <CloseCircleOutlined
              onClick={() => setsearchInput("")}
              className="search-clear"
            />
          )
        }
        className="visitor-search"
        value={searchInput}
        onChange={(e) => setsearchInput(e.target.value)}
        allowClear={false}
      />
      <FilterOutlined className="filter-icon" />
      <Select
        value={typeFilter}
        onChange={changeTypeFilter}
        className="filter-select"
      >
        <Select.Option value="all">All types</Select.Option>
        {typeOptions.map((t) => (
          <Select.Option key={t} value={t}>{t}</Select.Option>
        ))}
      </Select>
      <Select
        value={statusFilter}
        onChange={changeStatusFilter}
        className="filter-select"
      >
        <Select.Option value="all">All statuses</Select.Option>
        {VISITOR_STATUS_OPTIONS.map((o) => (
          <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
        ))}
      </Select>
      {/* Filtered in SQL against the same expressions the badge is built from,
          so what this returns is exactly what the Induction column shows. */}
      <Select
        value={inductionFilter}
        onChange={changeInductionFilter}
        className="filter-select"
      >
        <Select.Option value="all">All inductions</Select.Option>
        {INDUCTION_FILTER_OPTIONS.map((o) => (
          <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
        ))}
      </Select>
    </div>
  );

  // Manual checkout is a management action, gated to the same roles as
  // Add Visitor. Other row actions (view summary etc.) stay open.
  const canManageVisitors =
    activePermission == "owner" || activePermission == "admin";

  const renderRow = (v) => {
    const status = getVisitorStatus(v);
    const meta = VISITOR_STATUS_META[status];
    const type = getVisitorType(v);
    const inductionState = getVisitorInductionState(v);
    const inductionMeta = INDUCTION_STATE_META[inductionState];
    const inductionSummary = v.induction_summary || {};
    const canCheckOut =
      status === VISITOR_STATUS.ONSITE || status === VISITOR_STATUS.OVERDUE;
    const contactTag = v.division_name && DIVISION_CONTACT_TAG[v.division_contact_status];
    const canConfirmContact = !!contactTag && v.division_contact_status !== "verified" && !v.checkout_time;

    return (
      <tr key={v.id}>
        <td>
          <div className="visitor-cell">
            <div
              className="visitor-avatar"
              style={{ background: avatarColor(v.name) }}
            >
              {initials(v.name)}
            </div>
            <div className="visitor-meta">
              <div className="visitor-name">{v.name || "—"}</div>
              {v.email && <div className="visitor-sub">{v.email}</div>}
              {v.phone && <div className="visitor-sub">{v.phone}</div>}
            </div>
          </div>
        </td>
        <td>
          <span className={`type-badge ${VISITOR_TYPE_CLASS[type] || "type-other"}`}>
            {type}
          </span>
        </td>
        <td>
          {/* Contractor check-in division (property QR landing). */}
          {v.division_name || "—"}
          {contactTag && (
            <div>
              {v.division_contact_status === "verified" ? (
                <Tooltip
                  title={`Confirmed by ${v.division_contact_verified_by_name || "unknown"} at ${fmtDateTime(v.division_contact_verified_at)}`}
                >
                  <Tag color={contactTag.color}>{contactTag.label}</Tag>
                </Tooltip>
              ) : (
                <Tag color={contactTag.color}>{contactTag.label}</Tag>
              )}
              {v.division_contact_escalated_at && v.division_contact_status !== "verified" && (
                <Tag color="red" title={`Escalated ${fmtDateTime(v.division_contact_escalated_at)}`}>
                  Escalated
                </Tag>
              )}
            </div>
          )}
        </td>
        <td>
          <span className={`status-pill ${meta.className}`}>
            <span className="status-dot" />
            {meta.label}
          </span>
        </td>
        <td className="ts-cell">
          {v.checkin_time ? (
            fmtDateTime(v.checkin_time)
          ) : v.scheduled_arrival ? (
            // Not on site yet — show the booked slot so the Schedule
            // tab reads as a plan rather than a column of dashes.
            <span className="scheduled-hint">
              Scheduled: {fmtDateTime(v.scheduled_arrival)}
            </span>
          ) : (
            "—"
          )}
        </td>
        <td className="ts-cell muted">{fmtDateTime(v.checkout_time)}</td>
        <td>
          {inductionState === INDUCTION_STATE.NOT_RECORDED ||
          inductionState === INDUCTION_STATE.EMPTY ? (
            // Two different nothings, both silent in this column. NOT_RECORDED
            // predates induction tracking — nobody failed to induct them, the
            // system was not keeping the record. EMPTY means nothing was ever
            // asked: no requirement matches them and their check-in form embeds
            // no induction. Neither is an action for anyone, so neither gets an
            // icon or a popover.
            <span
              className={inductionMeta.className}
              title={
                inductionState === INDUCTION_STATE.EMPTY
                  ? "No induction is required for this visitor"
                  : "This visit predates induction tracking"
              }
            >
              —
            </span>
          ) : (
            <Popover
              // Only worth a popover when there is a required set to break
              // down; otherwise the badge already says everything.
              content={
                inductionSummary.required_total > 0 ? (
                  <div className="induction-breakdown">
                    {(inductionSummary.items || []).map((item) => (
                      <div key={item.form_id} className="induction-breakdown-row">
                        <span className={INDUCTION_STATE_META[item.state]?.className}>
                          {item.state === INDUCTION_STATE.COMPLETED
                            ? "✓"
                            : item.state === INDUCTION_STATE.PENDING
                            ? // Held, but waiting on a person. A hollow circle
                              // would say "not done", which is wrong — they did it.
                              "◑"
                            : item.state === INDUCTION_STATE.EXPIRING
                            ? "⚠"
                            : item.state === INDUCTION_STATE.REJECTED
                            ? "✕"
                            : "○"}
                        </span>{" "}
                        {item.form_name || "Induction"}
                        {item.state === INDUCTION_STATE.EXPIRED && " · expired"}
                        {item.state === INDUCTION_STATE.EXPIRING && " · expiring soon"}
                        {item.state === INDUCTION_STATE.PENDING && " · awaiting verification"}
                        {item.state === INDUCTION_STATE.REJECTED && " · rejected"}
                      </div>
                    ))}
                  </div>
                ) : null
              }
              trigger={inductionSummary.required_total > 0 ? "hover" : []}
            >
              <span className={inductionMeta.className} title={getInductionBadgeHint(inductionState)}>
                {inductionState === INDUCTION_STATE.COMPLETED ? (
                  <CheckCircleOutlined />
                ) : inductionState === INDUCTION_STATE.EXPIRED ||
                  inductionState === INDUCTION_STATE.REJECTED ? (
                  <WarningOutlined />
                ) : (
                  <ClockCircleOutlined />
                )}{" "}
                {getInductionBadgeLabel(inductionSummary, inductionState)}
                {/* The form name is deliberately NOT shown here. The column
                    answers "where does this visitor stand", and a form name is
                    both the widest thing in the row and the least useful part of
                    that answer. Which induction it was lives in View Summary,
                    and in the popover breakdown when a required set exists. */}
                {inductionSummary.completed_at &&
                  inductionState === INDUCTION_STATE.COMPLETED &&
                  !inductionSummary.required_total && (
                    <span className="induction-date">
                      {fmtDateTime(inductionSummary.completed_at)}
                    </span>
                  )}
              </span>
            </Popover>
          )}
        </td>
        <td>
          <div className="actions-cell">
            {canConfirmContact && (
              <Popconfirm
                title="Confirm this contractor made contact with their division contact?"
                onConfirm={() => handleConfirmDivisionContact(v)}
                okText="Confirm"
                cancelText="Cancel"
              >
                <Button loading={confirmingContactId === v.id}>
                  Confirm contact
                </Button>
              </Popconfirm>
            )}
            {canManageVisitors && (
              <Popconfirm
                title="Confirm Check Out?"
                onConfirm={() => handleManualCheckOut(v)}
                okText="Check out"
                cancelText="Cancel"
                disabled={!canCheckOut}
              >
                <Button
                  className="checkout-btn"
                  icon={<UserDeleteOutlined />}
                  disabled={!canCheckOut}
                  loading={checkingOutId === v.id}
                >
                  Manual Check Out
                </Button>
              </Popconfirm>
            )}
            <Dropdown overlay={menuList(v)} trigger={["click"]}>
              <Button className="overflow-btn" icon={<EllipsisOutlined />} />
            </Dropdown>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <LayoutWrapper>
      <VisitorPageWrapper>
        <Box id="containerBox" className="visitor-box">
          {/* Breadcrumb */}
          <div className="visitor-breadcrumb">
            <EnvironmentOutlined />
            <span>Property Settings</span>
            <span className="crumb-sep">›</span>
            <span className="crumb-current">Visitor Management</span>
            {activeProperty?.name && (
              <>
                <span className="crumb-sep">·</span>
                <span className="crumb-property">{activeProperty.name}</span>
              </>
            )}
          </div>

          {/* Header */}
          <div className="visitor-header">
            <div>
              <h2 className="visitor-title">Visitors</h2>
              <div className="visitor-subtitle">
                Track and manage everyone who visits this property.
              </div>
            </div>
            <div className="header-actions">
              {renderOptionsProperty()}
              {(activePermission == "owner" || activePermission == "admin") && (
                <Button
                  icon={<PlusOutlined />}
                  type="primary"
                  className="btn-addVisitor"
                  onClick={showModal}
                >
                  <IntlMessages id="visitorPage.addVisitor" />
                </Button>
              )}
            </div>
          </div>

          {!hasLoadedOnce ? (
            <Loader />
          ) : (
            <>
              {renderStatCards()}
              {renderTabs()}
              {renderFilterBar()}

              <div className="visitor-table-wrap">
                {loadingData ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
                    <Spin />
                  </div>
                ) : displayedVisitors.length > 0 ? (
                  <>
                    <table className="visitor-table">
                      <thead>
                        <tr>
                          <th>Visitor</th>
                          <th>Type</th>
                          <th>Division</th>
                          <th>Status</th>
                          <th>Arrival</th>
                          <th>Departure</th>
                          <th>Induction</th>
                          <th className="actions-head">Actions</th>
                        </tr>
                      </thead>
                      <tbody>{displayedVisitors.map(renderRow)}</tbody>
                    </table>
                    <div className="pagination-wrap">
                      <Pagination
                        current={currentPage}
                        total={totalCount}
                        pageSize={pageSize}
                        onChange={(page) => setCurrentPage(page)}
                        showSizeChanger
                        pageSizeOptions={["10", "20", "50", "100"]}
                        onShowSizeChange={(cur, size) => {
                          setCurrentPage(1);
                          setPageSize(size);
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: "48px 0" }}
                  />
                )}
              </div>
            </>
          )}
        </Box>

        <RunInductionModal
          visible={visibleInductionModal}
          visitor={inductionVisitor}
          propertyId={activePropertyId}
          onClose={closeInductionModal}
          onCompleted={updateVisitorData}
        />
        <VisitorInfoModal
          visibleVisitorModal={visibleVisitorModal}
          cancelModal={cancelModal}
          propertyId={activePropertyId}
          editedVisitor={visitorData}
          updateVisitorData={updateVisitorData}
        />
        <ViewSurveyResultModal
          visibleViewSurveyResult={visibleVisitorFromResultModal}
          cancelViewSurveyResult={cancelViewSurveyResult}
          surveyData={visitorForm}
          type={visitorForm?.form?.type}
        />
        <UserAccessModal
          visibleModal={visibleModal}
          setvisibleModal={setvisibleModal}
          viewSummary={viewSummary}
        />
        {visibleViewSummaryForm ? (
          <VisitorViewSummary
            viewSummary={viewSummary}
            visibleViewSummaryForm={visibleViewSummaryForm}
            setVisibleViewSummaryForm={setVisibleViewSummaryForm}
            // Verification happens in the drawer, next to the answers. An
            // owner/admin may accept any induction on the property; the person
            // who ran one may accept their own (the server allows it, flags it
            // as self-verified, and is the authority either way).
            canVerify={canManageVisitors}
            currentUserId={currentUser && currentUser.id}
            onVerified={updateVisitorData}
          />
        ) : (
          ""
        )}
      </VisitorPageWrapper>
    </LayoutWrapper>
  );
};

export default Visitor;
